import { useMemo, useState, type ReactElement } from 'react';
import { ActionIcon, Alert, Button, Divider, Drawer, Group, Loader, NumberInput, ScrollArea, Select, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { AlertTriangle, CheckCircle2, CopyPlus } from 'lucide-react';
import type { LicenseCostPageViewModel } from './LicenseCostPageViewModel';
import type { LicenseCostRecord, SeatCostEntryInput } from './LicensesApiService';
import { makeCurrencyFormatter } from './CostFormatting';
import { rawDescription, recordSeatCount } from './CostRecordDimensions';

interface SeatCostDrawerProps {
    vm: LicenseCostPageViewModel;
    opened: boolean;
    onClose: () => void;
}

/**
 * Admin entry for flat seat/subscription cost — the manual, universal source for License ROI
 * when a provider's billing export can't be read automatically. Writes an `admin` cost record
 * (tagged `cost_type: subscription`) that survives the daily provider-cost sync. Also lists
 * previously recorded manual entries so admins can see what's already captured.
 */
export default function SeatCostDrawer({ vm, opened, onClose }: SeatCostDrawerProps): ReactElement {
    const monthOptions = useMemo(() => recentMonths(6), []);
    const [connectionId, setConnectionId] = useState<string | null>(null);
    const [amount, setAmount] = useState<number | string>('');
    const [currency, setCurrency] = useState('USD');
    const [seatCount, setSeatCount] = useState<number | string>('');
    const [month, setMonth] = useState<string>(monthOptions[0]?.value ?? '');
    const [description, setDescription] = useState('');

    const amountNum = typeof amount === 'number' ? amount : Number.parseFloat(amount);
    const seatNum = typeof seatCount === 'number' ? seatCount : Number.parseFloat(seatCount);
    const canSubmit =
        connectionId !== null && Number.isFinite(amountNum) && amountNum > 0 && currency.trim().length > 0 && month.length > 0 && !vm.saving;

    async function submit(): Promise<void> {
        if (connectionId === null || !Number.isFinite(amountNum) || month.length === 0) return;
        const parts = month.split('-');
        const year = Number(parts[0]);
        const mon = Number(parts[1]);
        if (!Number.isFinite(year) || !Number.isFinite(mon)) return;
        const label = vm.connections.find((c) => c.id === connectionId)?.label ?? connectionId;
        const entry: SeatCostEntryInput = {
            providerConnectionId: connectionId,
            periodStart: new Date(Date.UTC(year, mon - 1, 1)).toISOString(),
            periodEnd: new Date(Date.UTC(year, mon, 1)).toISOString(),
            amount: amountNum,
            currency: currency.trim().toUpperCase(),
            description: description.trim().length > 0 ? description.trim() : `${label} — seats (manual entry)`,
            ...(Number.isFinite(seatNum) && seatNum > 0 ? { seatCount: seatNum } : {}),
        };
        const ok = await vm.submitSeatCost(entry);
        if (ok) {
            setAmount('');
            setSeatCount('');
            setDescription('');
        }
    }

    function connectionLabel(id: string): string {
        return vm.connections.find((c) => c.id === id)?.label ?? id;
    }

    const visibleHistory = connectionId === null ? vm.seatCostHistory : vm.seatCostHistory.filter((r) => r.providerConnectionId === connectionId);

    /** Prefill the form from a past entry — everything except the month, which stays as picked. */
    function autofill(record: LicenseCostRecord): void {
        setConnectionId(record.providerConnectionId);
        setAmount(record.amount);
        setCurrency(record.currency);
        setSeatCount(recordSeatCount(record) ?? '');
        setDescription(rawDescription(record) ?? '');
    }

    return (
        <Drawer opened={opened} onClose={onClose} position="right" size="md" padding="lg" title="Seat / subscription costs">
            <Stack gap="md">
                <Text size="xs" c="dimmed">
                    Enter a flat monthly seat cost for a connection. It feeds License ROI as subscription cost and survives the daily
                    provider-cost sync — use it where a provider&apos;s billing can&apos;t be read automatically.
                </Text>
                <Select
                    label="Connection"
                    placeholder={vm.connections.length === 0 ? 'No connections' : 'Select connection'}
                    data={vm.connections.map((c) => ({ value: c.id, label: c.label }))}
                    value={connectionId}
                    onChange={setConnectionId}
                    searchable
                />
                <Group grow align="end">
                    <NumberInput label="Monthly cost" placeholder="0.00" value={amount} onChange={setAmount} min={0} decimalScale={2} thousandSeparator="," />
                    <TextInput label="Currency" value={currency} onChange={(e) => setCurrency(e.currentTarget.value)} maxLength={3} />
                    <NumberInput label="Seats" placeholder="optional" value={seatCount} onChange={setSeatCount} min={0} />
                </Group>
                <Select label="Month" data={monthOptions} value={month} onChange={(value) => setMonth(value ?? '')} />
                <TextInput
                    label="Description"
                    placeholder="e.g. Copilot Business seats — engineering org"
                    value={description}
                    onChange={(e) => setDescription(e.currentTarget.value)}
                    maxLength={200}
                />
                <Group justify="flex-end">
                    <Button onClick={() => void submit()} loading={vm.saving} disabled={!canSubmit}>
                        Save
                    </Button>
                </Group>
                {vm.saveError !== null && (
                    <Alert color="red" icon={<AlertTriangle size={16} />} py="xs">
                        {vm.saveError}
                    </Alert>
                )}
                {vm.saveSuccess !== null && (
                    <Alert color="green" icon={<CheckCircle2 size={16} />} py="xs">
                        {vm.saveSuccess}
                    </Alert>
                )}

                <Divider />
                <div>
                    <Text size="sm" fw={600}>
                        Recorded entries
                    </Text>
                    <Text size="xs" c="dimmed">
                        {connectionId === null ?
                            'Manual seat costs from the last 12 months.'
                        :   `Manual seat costs for ${connectionLabel(connectionId)} from the last 12 months.`}
                    </Text>
                </div>
                {vm.historyLoading ?
                    <Group justify="center" py="md">
                        <Loader size="sm" />
                    </Group>
                : vm.historyError !== null ?
                    <Alert color="red" icon={<AlertTriangle size={16} />} py="xs">
                        {vm.historyError}
                    </Alert>
                : visibleHistory.length === 0 ?
                    <Text size="sm" c="dimmed">
                        {connectionId === null ? 'No manual seat costs recorded yet.' : 'No manual seat costs recorded for this connection yet.'}
                    </Text>
                :   <ScrollArea.Autosize mah={300} type="auto">
                        <Table verticalSpacing={6} horizontalSpacing="xs">
                            <Table.Tbody>
                                {visibleHistory.map((record, index) => (
                                    <Table.Tr key={`${record.id}:${index}`}>
                                        <Table.Td>
                                            <Text size="xs">{monthLabel(record.periodStart)}</Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs">{connectionLabel(record.providerConnectionId)}</Text>
                                        </Table.Td>
                                        <Table.Td style={{ textAlign: 'right' }}>
                                            <Text size="xs" ff="monospace">
                                                {makeCurrencyFormatter(record.currency)(record.amount)}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td style={{ width: 32 }}>
                                            <Tooltip label="Copy into form" withArrow>
                                                <ActionIcon size="sm" variant="subtle" aria-label="Copy entry into form" onClick={() => autofill(record)}>
                                                    <CopyPlus size={14} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea.Autosize>
                }
            </Stack>
        </Drawer>
    );
}

function monthLabel(periodStart: string): string {
    return new Date(periodStart).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function recentMonths(count: number): { value: string; label: string }[] {
    const now = new Date();
    const options: { value: string; label: string }[] = [];
    for (let i = 0; i < count; i += 1) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
        const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
        options.push({ value, label: d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) });
    }
    return options;
}
